import {
  Card,
  Carousel,
  ECardBorderRadiusSize,
  ECardMediaSize,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import NextLink from 'next/link';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import parkPlaceholder1 from '@/assets/escapes/park-placeholder/park-placeholder1.jpg';
import parkPlaceholder2 from '@/assets/escapes/park-placeholder/park-placeholder2.jpg';
import parkPlaceholder3 from '@/assets/escapes/park-placeholder/park-placeholder3.jpg';
import parkPlaceholder4 from '@/assets/escapes/park-placeholder/park-placeholder4.jpg';
import parkPlaceholder5 from '@/assets/escapes/park-placeholder/park-placeholder5.jpg';
import parkPlaceholder6 from '@/assets/escapes/park-placeholder/park-placeholder6.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { INearbyPark } from '@/hooks/useNearbyParks';
import formatImageURL from '@/utility/formatImageURL';
import { getCurrentSeason } from '@/utility/getCurrentSeason';

const refineNearbyPark = (pk: INearbyPark): pk is INearbyPark => {
  if (pk.slug && !!pk.slug && !!pk.metadata.address) return true;
  return false;
};

const getRandomParkPlaceholder = (idx: number, length = 1) => {
  const parks = [
    parkPlaceholder1.src,
    parkPlaceholder2.src,
    parkPlaceholder3.src,
    parkPlaceholder4.src,
    parkPlaceholder5.src,
    parkPlaceholder6.src,
  ];
  const safeLength = Math.max(1, Math.min(length, parks.length));
  return parks[idx % safeLength] || parkPlaceholder1.src;
};

const getSeasonalImageUrl = (parkData: INearbyPark) => {
  const currentSeason = getCurrentSeason();
  const foundImage = parkData.metadata.images?.find(
    image => image?.season?.toLowerCase() === currentSeason,
  );
  return foundImage ? foundImage.url : parkData.metadata.images?.[0]?.url;
};

export const NearbyParks = ({
  nearbyNationalParks,
  city,
}: {
  nearbyNationalParks?: INearbyPark[];
  city: string;
}) => {
  const { isMobile } = useBreakpoint();

  if (!nearbyNationalParks?.length) return null;
  return (
    <section className="py-8 bg-white md:py-12">
      <MaxWidth className="mx-auto">
        <Heading
          variant={EHeadingStyleVariant.H4}
          className={`font-normal ${nearbyNationalParks.length < 3 ? 'md:mb-10' : ''}`}
          component="h2">
          <FormattedMessage
            id="bX8z8v"
            defaultMessage="Discover Nearby Attractions In {city}"
            values={{ city }}
          />
        </Heading>
        <Carousel
          content={nearbyNationalParks
            // make sure the parks we link to have enough information populated
            .filter(refineNearbyPark)
            .map((park, idx) => {
              const image =
                getSeasonalImageUrl(park) ||
                getRandomParkPlaceholder(idx, nearbyNationalParks.length);
              return (
                <NextLink key={park.id} href={`/guide/${park.slug}`} passHref legacyBehavior>
                  <Card borderRadiusSize={ECardBorderRadiusSize.Large} withBoxShadow={false}>
                    <Card.Media
                      image={formatImageURL(image, 'portrait192', 'fill')}
                      size={ECardMediaSize.Full}
                      fullRadius
                      className="!h-80 sm:!h-96 md:!h-100 lg:!h-128 [&>img]:object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 w-full !h-80 sm:!h-96 md:!h-100 lg:!h-128 rounded-[inherit] bg-gradient-to-b from-transparent via-transparent to-black/75" />
                    <Card.Content className="absolute top-0 left-0 right-0 h-full ml-6">
                      <div>
                        {/* hide this for now until we have a way to get the distance */}
                        {/* <Tag
                          label={
                            park.distanceInMiles <= 1
                              ? intl.formatMessage(
                                  {
                                    defaultMessage: '{distanceInMiles} mile away',
                                    id: 'JEeOxa',
                                  },
                                  { distanceInMiles: park.distanceInMiles },
                                )
                              : intl.formatMessage(
                                  {
                                    defaultMessage: '{distanceInMiles} miles away',
                                    id: 'zZeIjg',
                                  },
                                  { distanceInMiles: park.distanceInMiles },
                                )
                          }
                          className="mt-6"
                          variant={ETagVariant.White}
                        /> */}
                        <div className="absolute bottom-0 left-0 w-full mb-6 text-white">
                          <Text variant={ETextStyleVariant.LargeBold} className="mt-4">
                            {park.metadata.title || park.metadata.seo_title}
                          </Text>

                          <Text
                            variant={ETextStyleVariant.MediumRegular}
                            className="mt-2 capitalize">
                            {/* TODO: get the city name */}
                            {park.metadata.address.state_name}
                          </Text>
                        </div>
                      </div>
                    </Card.Content>
                  </Card>
                </NextLink>
              );
            })}
          slidesPerGroup={1}
          slidesPerView={isMobile ? 1.2 : 3}
          autoHideNavigationArrows
        />
      </MaxWidth>
    </section>
  );
};
